import * as React from "react";

import { makeStyles } from "@material-ui/styles";
import ViewIcon from "@material-ui/icons/RemoveRedEye";

import { PageHeader, PageFooter, BackButton, Button, showNotification } from "jazasoft";
// import { PageHeader, PageFooter, BackButton } from "jazasoft";

import MuiTable from "../../../components/MuiTable";
import Dialog from "@material-ui/core/Dialog";

import { useDeepEffect, useDeepMemo, useEntityPrivilege, useMutation, useQuery, useSafeState, useSetting, useTime } from "../../../hooks";
import { getDistinctValues, isEmpty, toFixed } from "../../../utils/helpers";
import CardHeader from "../../../components/CardHeader";
import { LedgerType } from "../../../utils/types";
import { format } from "../../../utils/dateUtils";
import useWarehouse from "../../../hooks/useWarehouse";
import { LedgerOutType, UOM } from "../../../constants";
import FabricIssueDrawer from "./FabricIssueDrawer";
import RollViewDrawer from "./RollViewDrawer";
import hasPrivilege from "../../../utils/hasPrivilege";
import { ResourceId, Scope } from "../../../utils/privilege";
import { convert } from "../../../helpers/unitConversionHelper";
import SettingKeys from "../../../utils/SettingKeys";

import missingImage from "../../../asset/img/image_not_available.png";
import { appUrl } from "../../../App";
import { colorTomatoRed } from "../../../utils/colors";

// icons
import DeleteIcon from "@material-ui/icons/Delete";
import { useDispatch } from "react-redux";

const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

const Preview = ({ visible, filename, fabricId, onClose, onDeleteFabricImage, colorId }) => {
  const onDeleteImage = () => {
    onDeleteFabricImage && onDeleteFabricImage(fabricId, filename, colorId);
  };

  return (
    <Dialog open={visible || false} onClose={onClose} fullWidth maxWidth={"md"}>
      {!isEmpty(filename) && (
        <div style={{ display: "flex", justifyContent: "center", padding: "0.75em" }}>
          <Button label="Delete" variant="outlined" style={{ color: colorTomatoRed.dark }} onClick={onDeleteImage}>
            <DeleteIcon />
          </Button>
        </div>
      )}
      {!isEmpty(filename) && <img src={`${appUrl}/files/${filename}?path=fabric/${fabricId}`} alt={filename} height="60%" />}
    </Dialog>
  );
};

const useStyles = makeStyles(theme => ({
  root: {},
  content: {
    margin: "1.5em",
    marginTop: 0
  },
  mrTop: {
    marginTop: "1.5em"
  }
}));

const renderStyleImage = (value, row, fabricId, onClick) => {
  const imgUrl = isEmpty(value) ? missingImage : `${appUrl}/files/${value}?path=fabric/${fabricId}`;
  const colorId = row?.id;
  return (
    <div onClick={() => !isEmpty(value) && onClick && onClick(value, fabricId, colorId)}>
      <img src={imgUrl} alt={value} style={{ height: 40, backgroundColor: isEmpty(value) ? "yellow" : "none" }} />
    </div>
  );
};

const FabricView = props => {
  const { id, roles, hasAccess, history } = props;
  const classes = useStyles();
  const routeState = props.location?.state || {};

  const [version, setVersion] = useSafeState(1);
  const [selectedColor, setSelectedColor] = useSafeState({});
  const [selectedInLedgerId, setSelectedInLedgerId] = useSafeState();
  const [drawerState, setDrawerState] = useSafeState({ open: false, name: null, ledger: null });
  const [dialogState, setDialogState] = useSafeState({
    active: false,
    name: null,
    filename: null,
    fabricId: null,
    colorId: null
  });

  const { disableCreate } = useEntityPrivilege("MaterialOut");
  const { disableEdit } = useEntityPrivilege("Fabric");
  const { utc_offset } = useTime();
  const { allWhList } = useWarehouse();
  const [mutate] = useMutation();
  const dispatch = useDispatch();

  const conversionFactor = useSetting(SettingKeys.UOM_CONVERSION_FACTOR);
  const conversionFactorList = JSON.parse(conversionFactor || "[]");

  const { data: fabric = {} } = useQuery({ url: `fabrics/${id}`, version });
  const { data: rollInfoData = [] } = useQuery(
    !isEmpty(selectedColor?.effColor) && {
      url: `rolls`,
      params: { size: 10000, search: `fabricItem.fabric.id==${id};fabricItem.color=='${selectedColor.color}';active=in=('TRUE', 'FALSE')` },
      version
    }
  );
  const { data: ledgers = [] } = useQuery(
    !isEmpty(selectedColor?.effColor) && {
      url: `ledgers`,
      params: { search: `fabric.id==${id};fabColor=='${selectedColor.effColor}'`, size: 10000, version }
    }
  );
  const { data: transferLedgers = [] } = useQuery(
    !isEmpty(fabric) &&
      !isEmpty(selectedColor?.effColor) && {
        url: `ledgers`,
        params: {
          search: `type=='${LedgerType.ITEM_TRANSFER}';toItemCode=='${fabric.itemCode}';toItemColor=='${selectedColor.color}'${
            !isEmpty(selectedColor.colorCode) ? `;toItemColorCode=='${selectedColor.colorCode}'` : ""
          }`,
          size: 10000
        }
      }
  );

  const orderIds = isEmpty(ledgers) ? [] : getDistinctValues(ledgers?.map(l => l.orderId).filter(id => !isEmpty(id)));
  const productIds = isEmpty(ledgers) ? [] : getDistinctValues(ledgers?.map(l => l.productId).filter(id => !isEmpty(id)));
  const { data: orderList = [] } = useQuery(!isEmpty(orderIds) && { url: `orders`, params: { search: `id=in=(${orderIds.join(",")})`, size: 1000 } });
  const { data: productList = [] } = useQuery(
    !isEmpty(productIds) && { url: `products`, params: { search: `id=in=(${productIds.join(",")})`, size: 1000 } }
  );

  const rollInfoList = useDeepMemo(() => {
    if (isEmpty(rollInfoData)) return [];
    const rollList = rollInfoData || [];
    return rollList?.filter(ri => ri.currEffItemColor === selectedColor?.effColor);
  }, [rollInfoData, fabric, selectedColor]);

  const onClick = (filename, fabricId, colorId) => {
    setDialogState({ active: true, name: "styleImage", filename, fabricId, colorId });
  };

  const infoColumns = useDeepMemo(() => {
    let columns = [
      { dataKey: "itemCode", title: "Item Code" },
      { dataKey: "effColor", title: "Fabric Color" },
      { dataKey: "articleName", title: "Article Name", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "itemDesc", title: "Item Description", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "classification", title: "Classification", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "fabricCategoryName", title: "Fabric Category", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "fabricPatternName", title: "Fabric Pattern", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "construction", title: "Count Contruction", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "content", title: "Content", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "repeat", title: "Repeat", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "gsm", title: "GSM", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "swatchImage", title: "swatchImage", render: (value, _, row) => renderStyleImage(value, row, id, onClick) }
    ];
    return columns;
  }, [version, fabric]);

  const infoRows = useDeepMemo(() => {
    if (isEmpty(fabric)) return [];
    const infoRows = fabric.colorList.map(fabricColor => ({
      ...fabric,
      ...fabricColor,
      fabricCategoryName: fabric.fabricCategory?.name,
      fabricPatternName: fabric.fabricPattern?.name || fabricColor.fabricPatternName,
      repeat:
        !isEmpty(fabricColor.repeatLength) && !isEmpty(fabricColor.repeatWidth)
          ? `${fabricColor.repeatLength} X ${fabricColor.repeatWidth}`
          : !isEmpty(fabricColor.repeatLength)
    }));
    return infoRows;
  }, [fabric, version]);

  useDeepEffect(() => {
    if (!isEmpty(routeState)) {
      setSelectedColor({ effColor: routeState.effColor, color: routeState.color, colorCode: routeState.code });
    }
  }, [routeState]);

  useDeepEffect(() => {
    if (!isEmpty(infoRows) && isEmpty(selectedColor)) {
      setSelectedColor({ effColor: infoRows[0]?.effColor, color: infoRows[0].color, colorCode: infoRows[0].code });
    }
  }, [infoRows]);

  const inwardRows = useDeepMemo(() => {
    if ((isEmpty(ledgers) && isEmpty(transferLedgers)) || isEmpty(fabric)) return [];
    const rows = [...ledgers, ...transferLedgers]
      .sort((a, b) => a.id - b.id)
      .filter(
        ledger =>
          ledger.type === LedgerType.IN ||
          (ledger.type === LedgerType.ITEM_TRANSFER &&
            ledger.toItemCode === fabric.itemCode &&
            ledger.toItemColor === selectedColor?.color &&
            `${ledger.toItemColorCode || ""}` === `${selectedColor?.colorCode || ""}`)
      )
      .map(ledger => {
        const outLedgers = ledgers.filter(l => l.fabricItem?.id === ledger?.fabricItem?.id).filter(ledger => ledger.type === LedgerType.OUT);
        const associatedQty = outLedgers.filter(l => l.type === LedgerOutType.BULK).reduce((acc, e) => acc + (e.ledgerQty || 0), 0);
        const associatedQtyInMeter = convert({
          input: associatedQty,
          uomIn: ledger?.fabricItem?.uomLength,
          uomOut: UOM.METER.value,
          gsm: ledger?.fabricItem?.orderingGsm,
          widthInCm: ledger?.fabricItem?.width,
          conversionFactorList
        });
        const allocatedQty = outLedgers
          .map(l =>
            l.outType === LedgerOutType.BULK ? l.allocatedQty || 0 : l.outType === LedgerOutType.OUTSOURCE_RETURN ? -(l.qty || 0) : l.qty || 0
          )
          .reduce((acc, qty) => acc + qty, 0);

        const invoiceQty = ledger?.fabricItem?.invoiceQty || 0;
        const invoiceQtyInMeter = convert({
          input: invoiceQty,
          uomIn: ledger?.fabricItem?.uomLength,
          uomOut: UOM.METER.value,
          gsm: ledger?.fabricItem?.orderingGsm,
          widthInCm: ledger?.fabricItem?.width,
          conversionFactorList
        });

        const grnQty = ledger.type === LedgerType.ITEM_TRANSFER ? ledger?.toFabricItem?.grnQty : ledger?.fabricItem?.grnQty;
        const grnQtyInMeter = convert({
          input: grnQty,
          uomIn: ledger?.fabricItem?.uomLength,
          uomOut: UOM.METER.value,
          gsm: ledger?.fabricItem?.orderingGsm,
          widthInCm: ledger?.fabricItem?.width,
          conversionFactorList
        });

        return {
          ...ledger,
          grnDate: !isEmpty(utc_offset) && !isEmpty(ledger?.fabricItem?.grnDate) ? format(ledger.fabricItem.grnDate, utc_offset, "dd MMM, yy") : "-",
          grn: ledger?.fabricItem?.grn,
          invoiceNo: ledger.invoice?.invoiceNo,
          supplierName: ledger.invoice?.supplier?.name,
          grnWarehouse: allWhList?.find(w => w.code === ledger.fabricItem?.warehouse)?.name || ledger.fabricItem?.warehouse,
          colorShade: ledger?.fabricItem?.colorShade,
          fabricPo: ledger?.fabricItem?.fabricPo,
          orderingWidth: ledger?.fabricItem?.orderingWidth,
          uomLength: ledger?.fabricItem?.uomLength,
          uomWidth: ledger?.fabricItem?.uomWidth,
          orderQty: ledger?.fabricItem?.orderQty,
          invoiceQty,
          invoiceQtyInMeter,
          grnQty,
          grnQtyInMeter,
          associatedQty,
          associatedQtyInMeter,
          allocatedQty,
          balance: Math.max(0, associatedQtyInMeter - allocatedQty)
        };
      });
    return rows;
  }, [ledgers, transferLedgers, fabric, selectedColor, utc_offset, allWhList]);

  useDeepEffect(() => {
    if (!isEmpty(inwardRows)) {
      setSelectedInLedgerId(inwardRows[0].id);
    }
  }, [inwardRows]);

  const inwardColumns = useDeepMemo(() => {
    let columns = [
      { dataKey: "txn", title: "TXN" },
      { dataKey: "grnDate", title: "GRN Date" },
      { dataKey: "grn", title: "GRN" },
      { dataKey: "invoiceNo", title: "Invoice No" },
      { dataKey: "supplierName", title: "Supplier", filterOptions: { filter: true } },
      { dataKey: "grnWarehouse", title: "GRN Warehouse", filterOptions: { filter: true } },
      { dataKey: "colorShade", title: "Color Shade", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "fabricPo", title: "Fabric PO", render: value => (isEmpty(value) ? "-" : value) },
      {
        dataKey: "orderingWidth",
        title: "Ordering Width",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uomWidth]}`)
      },
      { dataKey: "orderQty", title: "Fabric Order Qty", render: value => (isEmpty(value) ? "-" : value) },
      {
        dataKey: "invoiceQty",
        title: "Invoice Qty",
        render: (value, _, row) =>
          isEmpty(value)
            ? "-"
            : `${value} ${UomCodeFromValue[row.uomLength]} ${row.uomLength !== UOM.METER.value ? `(${toFixed(row.invoiceQtyInMeter)} m)` : ""}`
      },
      { dataKey: "fromItemCode", title: "From Item Code" },
      {
        dataKey: "fromItemColor",
        title: "From Item Color",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value}${!isEmpty(row.fromItemColorCode) ? `(${row.fromItemColorCode})` : ""}`)
      },
      {
        dataKey: "grnQty",
        title: "GRN Qty",
        render: (value, _, row) =>
          isEmpty(value)
            ? "-"
            : `${value} ${UomCodeFromValue[row.uomLength]} ${row.uomLength !== UOM.METER.value ? `(${toFixed(row.grnQtyInMeter)} m)` : ""}`
      },
      {
        dataKey: "associatedQty",
        title: "Associated Qty",
        render: (value, _, row) =>
          isEmpty(value)
            ? "-"
            : `${value} ${UomCodeFromValue[row.uomLength]} ${row.uomLength !== UOM.METER.value ? `(${toFixed(row.associatedQtyInMeter)} m)` : ""}`
      },
      { dataKey: "allocatedQty", title: "Allocated Qty", render: value => (!isEmpty(value) ? `${toFixed(value, 2)} m` : value) },
      { dataKey: "balance", title: "Balance to Allocate", render: value => (!isEmpty(value) ? `${toFixed(value, 4)} m` : value) }
    ];

    if (inwardRows.every(row => isEmpty(row.colorShade))) {
      columns = columns.filter(c => c.dataKey !== "colorShade");
    }
    if (inwardRows.every(row => isEmpty(row.fabricPo))) {
      columns = columns.filter(c => c.dataKey !== "fabricPo");
    }
    if (inwardRows.every(row => isEmpty(row.orderQty))) {
      columns = columns.filter(c => c.dataKey !== "orderQty");
    }
    if (isEmpty(transferLedgers)) {
      columns = columns.filter(c => c.dataKey !== "fromItemCode");
    }
    if (isEmpty(transferLedgers)) {
      columns = columns.filter(c => c.dataKey !== "fromItemColor");
    }

    return columns;
  }, [inwardRows, transferLedgers]);

  const issueRows = useDeepMemo(() => {
    if (isEmpty(ledgers) || isEmpty(fabric) || isEmpty(selectedInLedgerId)) return [];

    const inwardLedger = ledgers.find(ledger => ledger.id === selectedInLedgerId);

    const issueLedgers = ledgers
      .filter(ledger => ledger.fabricItem?.id === inwardLedger?.fabricItem?.id)
      .filter(ledger => ledger.type === LedgerType.OUT || ledger.type === LedgerType.ITEM_TRANSFER)
      .sort((a, b) => a.id - b.id)
      .map(ledger => {
        return {
          ...ledger,
          externalId: !isEmpty(ledger.externalIds) ? JSON.parse(ledger.externalIds)[0]?.externalId : "",
          effPartName: ledger.part,
          issueType: ledger.outType === "Bulk" ? "Bulk" : ledger.type === LedgerType.ITEM_TRANSFER ? "Item Transfer" : ledger.outType,
          poRef: orderList?.find(e => e.id === ledger.orderId)?.poRef || ledger.orderId,
          productName: productList?.find(e => e.id === ledger.productId)?.name,
          allocQty: ledger.outType === "Bulk" ? ledger.allocatedQty || 0 : ledger.outType === "Outsource-Return" ? -ledger.qty : ledger.qty,
          issueQty:
            ledger.outType === "Bulk" ? `${ledger.allocatedQty || 0}/${ledger.qty}` : ledger.outType === "Outsource-Return" ? -ledger.qty : ledger.qty
        };
      });

    if (issueLedgers?.length > 1) {
      const totalAllocQty = issueLedgers.reduce((acc, e) => acc + e.allocQty, 0);
      const totalRow = {
        issueType: "Total",
        issueQty: totalAllocQty,
        totalRow: true
      };
      return [...issueLedgers, totalRow];
    }

    return issueLedgers;
  }, [ledgers, fabric, selectedColor, selectedInLedgerId, orderList, productList]);

  const issueColumns = useDeepMemo(() => {
    let columns = [
      { dataKey: "txn", title: "TXN" },
      { dataKey: "externalId", title: "Ext. TXN" },
      { dataKey: "issueType", title: "Issue Type" },
      { dataKey: "sampleType", title: "Sample Type", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "poRef", title: "PO Ref", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "productName", title: "Product", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "style", title: "Style", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "styleColor", title: "Style Color", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "effPartName", title: "Part", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "toItemCode", title: "To Item Code", render: value => (isEmpty(value) ? "-" : value) },
      {
        dataKey: "toItemColor",
        title: "To Item Color",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value}${!isEmpty(row.toItemColorCode) ? `(${row.toItemColorCode})` : ""}`)
      },
      { dataKey: "challanNo", title: "Challan No", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "outDestination", title: "Out Destination", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "remarks", title: "Remarks", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "issueQty", title: "Fabric Qty" }
    ];

    if (issueRows.every(row => isEmpty(row.externalId))) {
      columns = columns.filter(c => c.dataKey !== "externalId");
    }
    if (issueRows.every(row => isEmpty(row.sampleType))) {
      columns = columns.filter(c => c.dataKey !== "sampleType");
    }
    if (issueRows.every(row => isEmpty(row.challanNo))) {
      columns = columns.filter(c => c.dataKey !== "challanNo");
    }
    if (issueRows.every(row => isEmpty(row.outDestination))) {
      columns = columns.filter(c => c.dataKey !== "outDestination");
    }
    if (issueRows.every(row => isEmpty(row.remarks))) {
      columns = columns.filter(c => c.dataKey !== "remarks");
    }
    if (issueRows.every(row => isEmpty(row.toItemCode))) {
      columns = columns.filter(c => c.dataKey !== "toItemCode");
    }
    if (issueRows.every(row => isEmpty(row.toItemColor))) {
      columns = columns.filter(c => c.dataKey !== "toItemColor");
    }

    return columns;
  }, [issueRows]);

  const onInlineActionClick = (event, action, row, onActionComplete) => {
    setDrawerState({ open: true, ledger: row });
  };

  const inlineActions = [{ name: "view", tooltip: "View Rolls", icon: <ViewIcon /> }];

  const hasMaterialOutPrivilege = hasPrivilege(roles, hasAccess, ResourceId.MATERIAL_OUT, Scope.WRITE);

  const onDeleteFabricImage = (fabricId, fileName, colorId) => {
    let url = `fabrics/${fabricId}/${colorId}`;
    let options = {
      url,
      method: "delete"
    };
    mutate(options, {
      onSuccess: () => {
        setDialogState({});
        setVersion(version + 1);
        dispatch(showNotification(`${fileName} Deleted Successfully`));
      }
    });
  };

  return (
    <div>
      <PageHeader title="Fabric View" />

      <Preview
        visible={dialogState.active && dialogState.name === "styleImage"}
        filename={dialogState.filename}
        fabricId={dialogState.fabricId}
        onClose={() => setDialogState({ active: false })}
        onDeleteFabricImage={onDeleteFabricImage}
        colorId={dialogState.colorId}
      />

      <FabricIssueDrawer
        open={drawerState?.open && !isEmpty(drawerState.name)}
        action={drawerState?.name}
        fabric={fabric}
        selectedColor={selectedColor}
        rollInfoList={rollInfoList}
        onRefresh={() => setVersion(v => v + 1)}
        onDrawerClose={() => setDrawerState({ open: false })}
      />

      <RollViewDrawer
        open={drawerState?.open && isEmpty(drawerState.name)}
        ledger={drawerState.ledger}
        fabric={fabric}
        selectedColor={selectedColor}
        rollInfoList={rollInfoList}
        roles={roles}
        hasAccess={hasAccess}
        onRefresh={() => setVersion(v => v + 1)}
        onDrawerClose={() => setDrawerState({ open: false })}
      />

      <div className={classes.content}>
        <MuiTable
          title={<CardHeader title="Fabric Details" style={{ marginLeft: 0 }} />}
          toolbar
          pageable={infoRows?.length > 10}
          columns={infoColumns}
          rows={infoRows}
          onRowClick={row => setSelectedColor({ effColor: row.effColor, color: row.color, colorCode: row.code })}
          rowStyle={({ row }) => (selectedColor?.effColor === row?.effColor ? { backgroundColor: "#BFDDF8" } : {})}
        />

        <div className={classes.mrTop}>
          <MuiTable
            title={<CardHeader title="Fabric Inward Details" style={{ marginLeft: 0 }} />}
            toolbar
            searchable
            searchKeys={["invoiceNo", "txn", "grn", "fabricPo", "fromItemCode"]}
            columns={inwardColumns}
            rows={inwardRows}
            pageable={inwardRows?.length > 10}
            onRowClick={row => setSelectedInLedgerId(row.id)}
            rowStyle={({ row }) => (selectedInLedgerId === row?.id ? { backgroundColor: "#FFEFE3" } : {})}
            inlineActions={inlineActions}
            onInlineActionClick={onInlineActionClick}
          />
        </div>

        <div className={classes.mrTop}>
          <MuiTable
            title={<CardHeader title="Fabric Issue Details" style={{ marginLeft: 0 }} />}
            toolbar
            columns={issueColumns}
            rows={issueRows}
            onRowClick={row => setDrawerState({ open: true, ledger: row })}
          />
        </div>

        <PageFooter>
          <BackButton variant="outlined" color="primary" />
          {hasPrivilege(roles, hasAccess, ResourceId.FABRIC, Scope.UPDATE) && !disableEdit && (
            <Button
              label="Edit"
              variant="outlined"
              color="primary"
              style={{ marginLeft: "1.5em" }}
              onClick={() => history.push(`/warehouse/fabrics/${id}/edit`)}
            />
          )}
          {hasMaterialOutPrivilege && !disableCreate && (
            <Button
              label="Sample Issue"
              variant="outlined"
              color="primary"
              style={{ marginLeft: "1.5em" }}
              onClick={() => setDrawerState({ open: true, name: "sample_issue" })}
            />
          )}
          {hasMaterialOutPrivilege && !disableCreate && (
            <Button
              label="Outsource"
              variant="outlined"
              color="primary"
              style={{ marginLeft: "1.5em" }}
              onClick={() => setDrawerState({ open: true, name: "outsource" })}
            />
          )}
          {hasMaterialOutPrivilege && !disableCreate && (
            <Button
              label="Return Supplier"
              variant="outlined"
              color="primary"
              style={{ marginLeft: "1.5em" }}
              onClick={() => setDrawerState({ open: true, name: "return_supplier" })}
            />
          )}
          {hasMaterialOutPrivilege && !disableCreate && (
            <Button
              label="Write Off"
              variant="outlined"
              color="primary"
              style={{ marginLeft: "1.5em" }}
              onClick={() => setDrawerState({ open: true, name: "write_off" })}
            />
          )}
        </PageFooter>
      </div>
    </div>
  );
};

export default FabricView;
